import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Icon } from '@iconify/react';
import countryData from '../../utils/countory.json';
import { getCountryDisplayName } from '../../utils/phoneCountryRules';

/**
 * Searchable dial-code picker (same UX as auth Register). Shows country name + code on separate columns.
 */
export default function CountryCodeDropdown({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);

  const filtered = useMemo(() => {
    const all = countryData?.countoryCode || [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (c) =>
        (c.country && c.country.toLowerCase().includes(q))
        || (c.code && c.code.toLowerCase().includes(q)),
    );
  }, [search]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (country) => {
    onChange(country);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="tw-relative country-code-wrapper" ref={wrapRef}>
      <div
        className={`tw-flex tw-items-center tw-justify-between tw-w-full tw-min-w-[90px] tw-h-[44px] tw-p-[10px_14px] tw-border-[1px] tw-border-solid tw-border-[var(--input-border)] tw-rounded-[8px] tw-bg-[#FFFFFF] tw-text-[16px] tw-leading-[24px] tw-text-[#101828] tw-cursor-pointer [transition:all_var(--transition-fast)] [&:hover]:tw-border-[var(--primary-color)] [&:focus]:tw-border-[var(--primary-color)] [&:focus]:tw-shadow-[0_0_0_4px_rgba(67,_24,_255,_0.1)] [&:focus]:[outline:none] country-code-select${disabled ? "   tw-opacity-50" : ""}`}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!disabled) setOpen((v) => !v);
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span>{value}</span>
        <Icon icon="mdi:chevron-down" className="tw-text-[#667085] tw-text-[16px] [transition:transform_0.2s_ease] dropdown-icon" />
      </div>
      {open && (
        <div className="tw-absolute tw-top-[100%] tw-left-0 tw-z-[1000] tw-w-[300px] tw-max-h-[300px] tw-border-[1px] tw-border-solid tw-border-[#e0e0e0] tw-rounded-[8px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.1)] tw-mt-[4px] tw-bg-[white] tw-right-0 country-dropdown" style={{ zIndex: 2000 }}>
          <div className={"[&_.auth-input-icon]:tw-absolute [&_.auth-input-icon]:tw-left-4 [&_.auth-input-icon]:tw-top-[50%] [&_.auth-input-icon]:[transform:translateY(-50%)] [&_.auth-input-icon]:tw-text-[#6c757d] tw-relative tw-p-[var(--spacing-md)] tw-border-b-[1px] tw-border-solid tw-border-b-[var(--border-color)] [&_input]:tw-w-full [&_input]:tw-p-[var(--spacing-sm)_var(--spacing-sm)_var(--spacing-sm)_var(--spacing-xl)] [&_input]:tw-pl-8 [&_input]:tw-text-[0.875rem] [&_input]:tw-border-[1px] [&_input]:tw-border-solid [&_input]:tw-border-[var(--border-color)] [&_input]:tw-rounded-[var(--radius-md)] [&_input]:tw-bg-[var(--bg-secondary)] [&_input]:tw-text-[var(--text-primary)] [&_input:focus]:[outline:none] [&_input:focus]:tw-border-[var(--primary-color)] [&_input:focus]:tw-bg-[var(--bg-primary)] country-search"}>
            <input
              type="text"
              placeholder="Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoComplete="off"
            />
          </div>
          <div className={"[&::-webkit-scrollbar]:tw-w-[6px] [&::-webkit-scrollbar-track]:tw-bg-[#f1f1f1] [&::-webkit-scrollbar-thumb]:tw-bg-[#888] [&::-webkit-scrollbar-thumb]:tw-rounded-[3px] [&::-webkit-scrollbar-thumb:hover]:tw-bg-[#555] tw-max-h-[210px] tw-overflow-y-auto country-list"} role="listbox">
            {filtered.map((country, index) => (
              <div
                key={`${country.code}-${index}`}
                className={"tw-flex tw-items-center tw-justify-between tw-p-[var(--spacing-sm)_var(--spacing-md)] tw-cursor-pointer tw-text-[0.875rem] tw-text-[var(--text-primary)] [&:hover]:tw-bg-[var(--bg-secondary)] country-item"}
                role="option"
                aria-selected={country.code === value}
                onClick={() => pick(country)}
              >
                <span>{getCountryDisplayName(country)}</span>
                <span className="tw-text-[#6c757d] country-code">{country.code}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
